/**
 * #4981 phase 4 (#5270) — the way BACK: copying an Android device's local data out of
 * the native SQLite engine and into the browser's IndexedDB.
 *
 * **Read `design-docs/android-local-database-engine.md`, "The way back, the ramp, and the
 * cleanup (phase 4, #5270)", before changing anything here.** This
 * is the module that exists so the migration is not a one-way door, and a mistake in it
 * costs a user their logged workouts just as surely as one in the forward direction.
 *
 * ## What shape it takes, and why
 *
 * It MIRRORS the post-#5449 forward direction rather than being a boot-screen copy:
 *
 *  - a **background half** copies the bulk of the data while the app carries on running
 *    on SQLite, verifies it in full, and then follows the source so later writes keep
 *    reaching the new IndexedDB copy;
 *  - a **switch half** runs at the top of a later page load, before anything has opened
 *    a database. It replicates whatever is outstanding, verifies only that, and is the
 *    only half that may change which engine the device reads.
 *
 * A blocking copy on the boot screen was the shape R2 originally asked for, and it was
 * settled against on 2026-09-19: the measured account is about five minutes of copying,
 * the splash watchdog reloads the page at roughly 85 seconds, and #5449 deliberately
 * removed the suppression that made such a copy survivable. Blinding the watchdog again
 * would do it on exactly the devices already suspected of being sick. The full reasoning
 * is in the phase-4 plan's Implementation Notes.
 *
 * ## The three things that are NOT the same as the forward direction
 *
 *  1. **The target is a FRESH #4401 generation, not the same name.** The pre-migration
 *     IndexedDB databases still exist under exactly the names a reverse copy would
 *     otherwise write to, and the grace period exists to keep them. Deleting and
 *     recreating would destroy the fallback; merging into the stale copy would leave the
 *     verification unable to match.
 *  2. **The source handles are never closed.** The SQLite adapter closes the native
 *     connection BY NAME, so closing this copy's handle on a source would close the
 *     running app's connection to the same database underneath it. Forward, the target
 *     was SQLite and nothing else held it; backward, the app is reading the source all
 *     the way through. The shared copy machinery already leaves sources open for the
 *     IndexedDB version of this hazard (#5448) — it is the same rule, on the other side.
 *  3. **The tail is a resumable sequence with ONE commit point.** Which engine the device
 *     reads and which generation it reads are two independent `localStorage` values, and
 *     any order of two separate writes has a fatal middle. See
 *     {@link RollbackCompletionRecord}.
 *
 * Angular-free and driven by injected dependencies, the same shape as
 * `sqlite-migration.ts`, so the whole thing can be driven from a node test. Every
 * JUDGEMENT it makes lives in `services/sqlite-migration-decisions.ts`.
 */

import { errorReportFields } from '../services/error-patterns';
import {
  CopyDirection,
  MigrationReporter,
  SqliteMigrationDeps,
  baseNameOf,
  copyDatabasesAsync,
  reReadControlsAsync,
  startFollowingSourcesAsync,
} from './sqlite-migration';
import {
  MigrationOutcome,
  MigrationPhase,
  ROLLBACK_COMPLETED_INFO,
  ROLLBACK_PROBLEM_NAMES,
  RollbackCompletionEntry,
  RollbackCompletionRecord,
  GenerationMove,
  RollbackOutcome,
  RolledBackMarker,
  decideGenerationMove,
  readMigrationTargetNames,
  readRollbackCompletionRecord,
  readRolledBackMarker,
  serialiseRollbackCompletionRecord,
  serialiseRolledBackMarker,
} from '../services/sqlite-migration-decisions';
import {
  LOCAL_DATABASE_ENGINE_VALUE_IDB,
  SQLITE_MIGRATION_ROLLED_BACK_KEY,
  SQLITE_MIGRATION_TARGETS_KEY,
  SQLITE_ROLLBACK_COMPLETION_KEY,
} from '../services/local-database-engine-decisions';

/** Every console line this rollback writes carries it. */
const LOG_PREFIX = 'SQLite rollback (phase 4): ';

/**
 * The things only a rollback does, on top of what the shared copy machinery needs —
 * move the #4401 generation, change which engine the device reads, and delete the
 * SQLite files it is leaving behind.
 *
 * Kept separate from {@link SqliteRollbackDeps} below only so that the two can be
 * combined with an intersection. See the comment there.
 */
interface SqliteRollbackExtras {
  /** which #4401 generation this device currently reads for a base name */
  readLocalStoreGeneration(baseName: string): number;
  /** the name a base name has at a given generation */
  localStoreNameFor(baseName: string, generation: number): string;
  /**
   * Move this device onto the next generation of a base name, answering the name it is
   * now on.
   *
   * Injected rather than imported so the tail can be driven from a test — and because
   * this is the one call in the whole rollback that is NOT idempotent. It increments
   * unconditionally, which is why `decideGenerationMove` is asked for every base name
   * before any of them is moved.
   */
  bumpLocalStoreGeneration(baseName: string): string;
  /** write the engine marker, which is what actually moves the device */
  setEngineMarker(value: string): void;
  /**
   * Delete one IndexedDB database outright, used to clear a partial target before a
   * retry. The mirror of the forward direction's `deleteSqliteDatabaseAsync`, which this
   * direction ALSO needs — for the sources it leaves behind at the end.
   */
  deleteIndexedDbDatabaseAsync(logicalName: string): Promise<unknown>;
  /**
   * The re-arm generation to record in the rolled-back marker, or `undefined` when the
   * request marker could not be read.
   *
   * Decided on the session that ASKED for the rollback — the only one with the config
   * loaded — and carried here in the request marker's value, because the boot path this
   * runs on cannot read config. `undefined` stops the rollback committing rather than
   * guessing; `RollbackOutcome.ReArmGenerationUnknown` says why there is no safe guess.
   */
  reArmGeneration: number | undefined;
}

/**
 * Everything the way back needs from the outside world.
 *
 * The copy dependencies are reused rather than restated: the reverse copy IS the forward
 * copy pointed the other way, and anything stated twice would eventually be stated
 * differently.
 *
 * An intersection rather than an `extends`, because {@link SqliteMigrationDeps} is a
 * union discriminated on `phase` and a TypeScript interface cannot extend a union. An
 * intersection distributes over it, so a rollback's deps are still either the switch
 * shape or the background shape, and `deps.phase === …` still narrows here exactly as it
 * does on the forward side.
 */
export type SqliteRollbackDeps = SqliteMigrationDeps & SqliteRollbackExtras;

/** What one rollback attempt did. */
export interface RollbackResult {
  outcome: RollbackOutcome;
  /** the underlying failure behind an abort, when there was one */
  error?: unknown;
  /** how many databases were copied back and verified */
  databasesCopied: number;
  /** how many documents crossed, summed over the databases */
  documentsCopied: number;
  /**
   * The databases this run copied and verified, by their SQLite source name.
   *
   * The background half hands these to the follow, so it follows exactly what was
   * verified rather than re-deriving the list and risking a different answer.
   */
  copiedDatabaseNames?: string[];
}

/**
 * Turn the shared copy machinery's outcome into this direction's.
 *
 * ONE place, so the translation is visible rather than scattered — and so that adding an
 * outcome to either enum surfaces here as a compile error rather than as a rollback
 * failure quietly filed under a migration's problem name.
 */
function toRollbackOutcome(outcome: MigrationOutcome): RollbackOutcome {
  switch (outcome) {
    case MigrationOutcome.Completed: return RollbackOutcome.Completed;
    case MigrationOutcome.ConfigSaysNo: return RollbackOutcome.ConfigSaysNo;
    case MigrationOutcome.ConfigReadFailed: return RollbackOutcome.ConfigReadFailed;
    case MigrationOutcome.NoSignedInUser: return RollbackOutcome.NoSignedInUser;
    case MigrationOutcome.LatchAlreadyRead: return RollbackOutcome.LatchAlreadyRead;
    case MigrationOutcome.LatchTakenDuringCopy: return RollbackOutcome.LatchTakenDuringCopy;
    case MigrationOutcome.AttachmentsFound: return RollbackOutcome.AttachmentsFound;
    case MigrationOutcome.BoundHit: return RollbackOutcome.BoundHit;
    case MigrationOutcome.VerificationMismatch: return RollbackOutcome.VerificationMismatch;
    case MigrationOutcome.CopyFailed: return RollbackOutcome.CopyFailed;
    case MigrationOutcome.BulkCopyNotReady: return RollbackOutcome.BulkCopyNotReady;
    case MigrationOutcome.TargetRestarted: return RollbackOutcome.TargetRestarted;
    case MigrationOutcome.Cancelled: return RollbackOutcome.Cancelled;

    // The forward direction's enumeration reads `indexedDB.databases()`; this direction
    // enumerates its sources from the sign-out record instead, so these three cannot
    // arise here. They are mapped rather than left to a default so that the switch stays
    // exhaustive and a new forward outcome is a compile error rather than a silent
    // fall-through.
    case MigrationOutcome.EnumerationFailed:
    case MigrationOutcome.OwnDatabaseMissing:
    case MigrationOutcome.CommonDatabaseMissing:
      return RollbackOutcome.NoSourcesRecorded;

    // Forward-only: there is nothing to record for an IndexedDB target, so the shared
    // loop never asks. See `CopyDirection.recordTargetName`.
    case MigrationOutcome.TargetRecordFailed:
      return RollbackOutcome.CopyFailed;

  }
}

/**
 * The reverse direction, for the shared copy machinery.
 *
 * The target name is the NEXT generation of each base name, and it is computed the same
 * way on every page load: the generation is only moved by the tail, long after the copy,
 * so a background copy that resumes days later writes into the same target it started.
 */
export function reverseCopyDirection(deps: SqliteRollbackDeps): CopyDirection {
  return {
    sourceAdapter: 'capacitor-sqlite',
    targetAdapter: 'idb',
    // The follow is shared machinery, so the names it reports under have to travel with
    // the direction. Without this a rollback's follow failures would be filed as MIGRATION
    // failures, and the ramp counts the two apart precisely so it can tell "devices are
    // having trouble moving onto SQLite" from "devices are having trouble getting off it".
    followProblemNames: {
      failed: 'AndroidSqliteRollbackFollowFailed',
      denied: 'AndroidSqliteRollbackFollowDenied',
    },
    targetNameFor: (sourceLogicalName: string) => targetNameFor(deps, sourceLogicalName),
    deleteTargetDatabaseAsync: (targetLogicalName: string) =>
      deps.deleteIndexedDbDatabaseAsync(targetLogicalName),
    // deliberately absent: a reverse target is an ordinary IndexedDB database, and the
    // sign-out teardown enumerates and deletes every one of those on the origin. The
    // forward direction needs a note only because nothing can list the native SQLite
    // files. See `CopyDirection.recordTargetName`.
  };
}

/** The fresh generation this rollback copies into, for one source database. */
function targetNameFor(deps: SqliteRollbackDeps, sourceLogicalName: string): string {
  const baseName = baseNameOf(sourceLogicalName);
  return deps.localStoreNameFor(baseName, deps.readLocalStoreGeneration(baseName) + 1);
}

/**
 * Which SQLite databases this device has, by logical name.
 *
 * **Read from the sign-out record, not by enumeration**, because nothing can enumerate
 * them: the native plugin has no "list the databases" call, and the database provider
 * never held the ones the forward copy created. That record is written before anything is
 * copied into a target and is kept for as long as the files exist, so it is the one
 * complete answer on the device — which is also why it survives both `localStorage`
 * wipes.
 *
 * Filtered to THIS generation, so a #4401 reset's abandoned databases are left alone.
 */
export function sqliteSourceNames(deps: SqliteRollbackDeps): string[] {
  const recorded = readMigrationTargetNames(deps.storage.getItem(SQLITE_MIGRATION_TARGETS_KEY));
  return recorded.filter(
    name => name === deps.currentLocalStoreName(baseNameOf(name)),
  );
}

/**
 * Run one half of the rollback. Never throws — it is called on the boot path, and a throw
 * here would surface as an unhandled rejection in the middle of a boot.
 *
 * The engine marker is NOT written here on its own: the switch half writes the
 * rollback-completion record and then runs the tail, and the marker moves inside that.
 */
export async function runSqliteRollbackAsync(
  deps: SqliteRollbackDeps,
): Promise<RollbackResult> {
  const sourceNames = sqliteSourceNames(deps);
  if (sourceNames.length === 0) {
    // Nothing names a SQLite database to read from. On a device that really is on SQLite
    // this is a problem worth knowing about — the sign-out record has been lost, which
    // also means sign-out can no longer delete those files — so it is reported rather
    // than treated as "nothing to do".
    return { outcome: RollbackOutcome.NoSourcesRecorded, databasesCopied: 0, documentsCopied: 0 };
  }

  const commonDbName = deps.currentLocalStoreName(`global_common-fr_${deps.uid}`);
  if (!sourceNames.includes(commonDbName)) {
    // The config lives in the common database, so without it the kill switch cannot be
    // consulted. Do NOT "recover" by opening it anyway: on the SQLite side that creates a
    // brand-new empty database, which would read no config, resolve to "do not roll
    // back", and leave a stray file behind.
    return { outcome: RollbackOutcome.NoSourcesRecorded, databasesCopied: 0, documentsCopied: 0 };
  }

  const controls = await reReadControlsAsync(commonDbName, deps);
  if ('outcome' in controls) {
    return {
      outcome: toRollbackOutcome(controls.outcome), error: controls.error,
      databasesCopied: 0, documentsCopied: 0,
    };
  }
  // Re-read, not trusted from the deciding session: this is what makes taking a person
  // back OFF the rollback allow-list take effect immediately rather than one boot later.
  if (!controls.migrate) {
    return { outcome: RollbackOutcome.ConfigSaysNo, databasesCopied: 0, documentsCopied: 0 };
  }

  const copied = await copyDatabasesAsync(sourceNames, deps);
  if (copied.outcome !== MigrationOutcome.Completed) {
    const outcome = toRollbackOutcome(copied.outcome);
    // `BulkCopyNotReady` is the ordinary state of a device part-way through a copy that
    // legitimately takes several sessions, and `Cancelled` is the app tearing down.
    // Reporting either would file a problem per launch per database on every rolling-back
    // device — exactly the shape the ramp is supposed to read as an incident.
    if (copied.failure && copied.failedDatabaseName
      && outcome !== RollbackOutcome.BulkCopyNotReady
      && outcome !== RollbackOutcome.Cancelled) {
      reportRollbackFailure(copied.failedDatabaseName, outcome, copied.failure, deps.reporter);
    }
    return {
      outcome,
      error: copied.failure?.error,
      databasesCopied: copied.databasesCopied,
      documentsCopied: copied.documentsCopied,
    };
  }

  if (deps.phase === MigrationPhase.Background) {
    // The background half is finished for now. It never changes anything the device
    // reads: the copy sits at a generation nothing has been told to use yet, and the
    // caller starts the follow so it keeps up until the switch half runs.
    return {
      outcome: RollbackOutcome.Completed,
      databasesCopied: copied.databasesCopied,
      documentsCopied: copied.documentsCopied,
      copiedDatabaseNames: copied.copiedDatabaseNames,
    };
  }

  // The latch, asked once more now the copy is over. Anything that opens a database asks
  // which engine this device uses, and the answer is fixed for the life of the page — so
  // if something asked while the switch half was running, this session is committed to
  // SQLITE and will keep reading and writing there. Committing the rollback would then
  // move the device to IndexedDB on the next boot without anything done after this
  // moment. The copy is thrown away instead; it costs one page load and nothing else,
  // because the IndexedDB target keeps what it has and resumes from its checkpoint.
  if (deps.hasEngineBeenRead()) {
    reportRollbackFailure(
      '', RollbackOutcome.LatchTakenDuringCopy, undefined, deps.reporter);
    return {
      outcome: RollbackOutcome.LatchTakenDuringCopy,
      databasesCopied: copied.databasesCopied,
      documentsCopied: copied.documentsCopied,
    };
  }

  const committed = commitRollback(sourceNames, deps);
  if (committed !== RollbackOutcome.Completed) {
    return {
      outcome: committed,
      databasesCopied: copied.databasesCopied,
      documentsCopied: copied.documentsCopied,
    };
  }

  const tail = resolveRollbackTail(deps);
  if (tail !== RollbackOutcome.Completed) {
    return {
      outcome: tail,
      databasesCopied: copied.databasesCopied,
      documentsCopied: copied.documentsCopied,
    };
  }

  deps.reporter.trackSilentInfo(ROLLBACK_COMPLETED_INFO, {
    databases_copied: copied.databasesCopied,
    documents_copied: copied.documentsCopied,
  });
  return {
    outcome: RollbackOutcome.Completed,
    databasesCopied: copied.databasesCopied,
    documentsCopied: copied.documentsCopied,
    copiedDatabaseNames: copied.copiedDatabaseNames,
  };
}

/**
 * Write the two notes that commit a rollback, in the order whose middle is harmless.
 *
 * **The rolled-back marker goes FIRST, and the completion record second.** Both writes
 * can fail, and a device killed between them has to land somewhere survivable:
 *
 *  - marker first, then killed: the device is held back from migrating and is still on
 *    SQLite with all of its data. Nothing has moved. The next page load rolls back again,
 *    resuming from the copy already made. Harmless.
 *  - record first, then killed: the next boot would finish the tail and put the device on
 *    IndexedDB, but nothing would hold it back — so the deciding check would see an
 *    Android device on IndexedDB inside the rollout band and migrate it straight back.
 *    That is the migrate-roll-back-migrate lap this marker exists to stop.
 *
 * The completion record is therefore the COMMIT POINT, and it is written last.
 */
export function commitRollback(sourceNames: readonly string[], deps: SqliteRollbackDeps): RollbackOutcome {
  const entries: { [baseName: string]: RollbackCompletionEntry } = {};
  const supersededIndexedDbNames: string[] = [];
  for (const sourceName of sourceNames) {
    const baseName = baseNameOf(sourceName);
    entries[baseName] = {
      targetGeneration: deps.readLocalStoreGeneration(baseName) + 1,
      // recorded explicitly, never re-derived: once the generation moves, the live name
      // list is the NEW generation and a delete built from it would match nothing
      oldSqliteName: sourceName,
    };
    // the IndexedDB databases at the CURRENT generation — the pre-migration copy phase 3
    // kept — become unreachable once the device moves to the next one. R3's cleanup is
    // what deletes them; this is the only record that can still name them.
    supersededIndexedDbNames.push(deps.currentLocalStoreName(baseName));
  }

  if (deps.reArmGeneration === undefined) {
    // fails closed — see `RollbackOutcome.ReArmGenerationUnknown`. Nothing has moved: the
    // copy sits at a generation nothing reads, the device is still on SQLite with all of
    // its data, and the next deciding session writes a readable request marker.
    reportRollbackFailure('', RollbackOutcome.ReArmGenerationUnknown, undefined, deps.reporter);
    return RollbackOutcome.ReArmGenerationUnknown;
  }

  // APPEND to whatever an earlier rollback recorded, never replace it (#5270 step 6b, R3).
  // A device can roll back, be re-armed by an operator, migrate again and roll back a
  // second time, all before the cleanup that consumes the list has run. This list is the
  // ONLY record that can still name those databases — the app's own provider iterates at
  // the CURRENT generation and looks straight past them — so an overwrite would leave the
  // first generation's full copy of the user's data on disk with nothing able to find it.
  //
  // A DAMAGED existing marker reads as an empty list, so the union cannot recover names
  // that were already unreadable. Accepted: there is nothing left to recover them from,
  // and the alternative — refusing to commit — would strand the device mid-rollback.
  const alreadyRecorded =
    readRolledBackMarker(deps.storage.getItem(SQLITE_MIGRATION_ROLLED_BACK_KEY))
      ?.supersededIndexedDbNames ?? [];
  const marker: RolledBackMarker = {
    generation: deps.reArmGeneration,
    // deduped, because the tail can be RESUMED: a device killed after the marker write and
    // before the completion record rolls back again from the same generation next time
    supersededIndexedDbNames: [...new Set([...alreadyRecorded, ...supersededIndexedDbNames])],
  };

  try {
    deps.storage.setItem(SQLITE_MIGRATION_ROLLED_BACK_KEY, serialiseRolledBackMarker(marker));
    deps.storage.setItem(
      SQLITE_ROLLBACK_COMPLETION_KEY,
      serialiseRollbackCompletionRecord({ entries }),
    );
  } catch (error) {
    // Fails CLOSED. Without the record there is nothing to tell the next boot what to
    // finish, so nothing is moved: the device still reads SQLite, everything is where it
    // was, and the next page load tries again. A browser with site data blocked throws
    // from the setter itself, and so does an exhausted quota.
    reportRollbackFailure('', RollbackOutcome.RecordWriteFailed, undefined, deps.reporter, error);
    return RollbackOutcome.RecordWriteFailed;
  }

  console.log(`${LOG_PREFIX}committed; ${sourceNames.length} databases to finish`);
  return RollbackOutcome.Completed;
}

/**
 * Finish a rollback whose commit point has been passed: steps 3-5 of R2's tail.
 *
 * **Called at the top of a page load, before the engine question has been asked**, and
 * again immediately after the switch half commits. Every step is idempotent, so a boot
 * killed anywhere in here leaves the next one able to finish it and no boot ever observes
 * the inconsistent middle:
 *
 *  3. move each base name onto the recorded generation — but only if it is not there
 *     already, because `bumpLocalStoreGeneration` increments unconditionally;
 *  4. write the engine marker back to `idb` — a marker already `idb` is unchanged;
 *  5. delete the SQLite databases at the RECORDED names, then clear the record — a delete
 *     of an absent database succeeds.
 *
 * Answers `Completed` when there was nothing to finish, because "no tail outstanding" is
 * the ordinary state of every boot on every device.
 *
 * Synchronous through step 4 on purpose: everything that decides what the device reads is
 * a `localStorage` write, and doing them without an await in between keeps the window in
 * which a page load could be killed as small as the platform allows. Step 5 is the
 * asynchronous part, and it is the one step that changes nothing the device reads.
 */
export function resolveRollbackTail(deps: SqliteRollbackDeps): RollbackOutcome {
  let record: RollbackCompletionRecord | undefined;
  try {
    record = readRollbackCompletionRecord(deps.storage.getItem(SQLITE_ROLLBACK_COMPLETION_KEY));
  } catch {
    // an unreadable store answers "nothing to finish", which leaves the device exactly
    // where it is — on SQLite, with its data. The engine accessor already reports an
    // unreadable marker at problem tier, so this does not report a second time.
    return RollbackOutcome.Completed;
  }
  if (!record) return RollbackOutcome.Completed;

  // Steps 3 and 4 are wrapped, because both of them WRITE. `setEngineMarker` is a bare
  // `localStorage` setter and a browser with site data blocked throws from it, so without
  // this the tail would throw out of a function the boot path is told never throws — and
  // it would do so having already moved the generation, which is precisely the
  // half-finished middle the completion record exists to make survivable. Answering with
  // an outcome instead leaves the record in place, so the next boot re-runs the tail from
  // the beginning and every step is idempotent.
  try {
    // ── step 3: move each base name onto the recorded generation ──
    //
    // **Every entry is asked BEFORE any of them is moved, and the two loops may not be
    // merged back into one.** A record holds one entry per base name and those entries
    // legitimately carry different target generations, because a #4401 reset bumps only the
    // user database. Deciding inside the acting loop means an entry that answers
    // `OutOfRange` can be reached AFTER an earlier entry has already been bumped — and
    // since that path returns before step 4, the device is left with one generation moved
    // and the engine marker still saying `sqlite`. It then opens a SQLite database at a name
    // nothing ever wrote, and it is permanent rather than transient: every later boot finds
    // the bumped entry `AlreadyThere` and the other still `OutOfRange`, so the marker is
    // never flipped and the tail never completes. If the moved one is the common database,
    // the config documents — including the rollback kill switch — read empty from then on.
    const moves = Object.entries(record.entries).map(([baseName, entry]) => ({
      baseName,
      entry,
      move: decideGenerationMove(deps.readLocalStoreGeneration(baseName), entry),
    }));

    const outOfRange = moves.find(({ move }) => move === GenerationMove.OutOfRange);
    if (outOfRange) {
      // The record cannot describe this device, so nothing at all is touched — see
      // `RollbackOutcome.GenerationOutOfRange`. The record stays, because it holds the only
      // names that can still find the superseded SQLite databases, and this reports on every
      // boot rather than moving the device on every boot.
      reportRollbackFailure(
        outOfRange.baseName, RollbackOutcome.GenerationOutOfRange, undefined, deps.reporter);
      return RollbackOutcome.GenerationOutOfRange;
    }

    for (const { baseName, entry, move } of moves) {
      if (move === GenerationMove.AlreadyThere) continue;

      const nameNowInUse = deps.bumpLocalStoreGeneration(baseName);
      const expectedName = deps.localStoreNameFor(baseName, entry.targetGeneration);
      if (nameNowInUse !== expectedName) {
        // A different failure from the one above: `bumpLocalStoreGeneration` is documented
        // to leave the app on the OLD name and answer with it when the write is REFUSED.
        // Carrying on would flip the engine marker and delete the SQLite files while the
        // device still reads the pre-rollback generation — the databases it is about to
        // need. The record stays, so the next boot retries from here.
        reportRollbackFailure(
          baseName, RollbackOutcome.GenerationBumpMismatch, undefined, deps.reporter);
        return RollbackOutcome.GenerationBumpMismatch;
      }
    }

    // ── step 4: the device now reads IndexedDB ──
    deps.setEngineMarker(LOCAL_DATABASE_ENGINE_VALUE_IDB);
  } catch (error) {
    reportRollbackFailure(
      '', RollbackOutcome.TailWriteFailed, undefined, deps.reporter, error);
    return RollbackOutcome.TailWriteFailed;
  }

  // ── step 5: delete the SQLite databases, then clear the record ──
  //
  // Deliberately NOT awaited, and deliberately last. Nothing the device reads depends on
  // it, the deletes are idempotent, and holding the top of a page load open for a native
  // plugin call is the frozen-boot failure this whole project exists to remove. What it
  // must not do is fail silently, so each failure is reported and the record is kept when
  // any delete failed, so the next boot tries again.
  void deleteSupersededSqliteDatabasesAsync(record, deps);

  console.log(`${LOG_PREFIX}finished; this device now reads IndexedDB`);
  return RollbackOutcome.Completed;
}

/**
 * Delete the SQLite databases a committed rollback left behind, and clear the record once
 * they are really gone.
 *
 * Keeping the record when a delete failed is what makes the next boot try again. Clearing
 * it regardless would leave a full copy of the user's data on the phone with nothing able
 * to name it — the engine marker already says `idb`, so the sign-out deleter is disarmed,
 * and the native plugin cannot list its own files.
 */
async function deleteSupersededSqliteDatabasesAsync(
  record: RollbackCompletionRecord,
  deps: SqliteRollbackDeps,
): Promise<void> {
  let allDeleted = true;
  for (const entry of Object.values(record.entries)) {
    try {
      // the native plugin is addressed by the STORAGE name, prefix and all
      await deps.deleteSqliteDatabaseAsync(`_pouch_${entry.oldSqliteName}`);
      console.log(`${LOG_PREFIX}deleted SQLite database _pouch_${entry.oldSqliteName}`);
    } catch (error) {
      allDeleted = false;
      deps.reporter.trackSilentProblem('AndroidSqliteRollbackSqliteDeleteFailed', {
        // the database NAME is fine to report; its contents never are
        db_name: entry.oldSqliteName,
        ...errorReportFields(error),
      });
    }
  }

  if (!allDeleted) return;
  try {
    deps.storage.removeItem(SQLITE_ROLLBACK_COMPLETION_KEY);
    // The sign-out record named the SQLite files, and they are gone, so it must go too —
    // otherwise sign-out asks the plugin to delete databases that no longer exist and
    // reports each refusal as a problem.
    deps.storage.removeItem(SQLITE_MIGRATION_TARGETS_KEY);
  } catch (error) {
    // The files ARE gone, so nothing is orphaned; what is left is a record that will be
    // re-run next boot and delete nothing. Worth knowing about, not worth failing for.
    deps.reporter.trackSilentProblem('AndroidSqliteRollbackRecordClearFailed', {
      ...errorReportFields(error),
    });
  }
}

/** Report one failure under its own problem name, with no document contents. */
function reportRollbackFailure(
  logicalName: string,
  outcome: RollbackOutcome,
  failure: { attachmentCount?: number; verification?: any; error?: unknown } | undefined,
  reporter: MigrationReporter,
  error?: unknown,
): void {
  const problemName = ROLLBACK_PROBLEM_NAMES[outcome] ?? 'AndroidSqliteRollbackFailed';
  reporter.trackSilentProblem(problemName, {
    // the database NAME is fine to report; its contents never are
    ...(logicalName === '' ? {} : { db_name: logicalName }),
    ...(failure?.attachmentCount === undefined
      ? {} : { attachment_count: failure.attachmentCount }),
    ...(failure?.verification === undefined ? {} : {
      source_count: failure.verification.sourceCount,
      target_count: failure.verification.targetCount,
      difference_count: failure.verification.differenceCount,
      // ids and how each differed — bounded, and never what a document said
      sample: failure.verification.sampleReasons.join(','),
    }),
    ...errorReportFields(error ?? failure?.error),
  });
}

/**
 * Start following the SQLite sources, so the new IndexedDB copy keeps up with everything
 * written while the app carries on running.
 *
 * A thin pass-through to the shared follow, here so the reverse direction has one entry
 * point and the caller never has to know which module owns what. The follow's own rules —
 * no silence bound, failures watched rather than awaited, stopped before sign-out and
 * before a reload — are stated on that function and apply unchanged.
 *
 * **One thing about this direction the forward one does not have.** The app's own writes
 * go through the app's SQLite handle, and this follow holds a different one; the adapter's
 * change listeners are per handle, so writes reach this follow on the adapter's 30-second
 * safety-net poll rather than immediately. That is not a problem — the switch half
 * replicates and verifies whatever is outstanding — but it is why nothing here promises
 * the copy is current "within moments".
 */
export function startFollowingSqliteSourcesAsync(
  deps: SqliteRollbackDeps,
  logicalNames: readonly string[],
) {
  return startFollowingSourcesAsync(deps, logicalNames);
}
